import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from '../components/SideDrawer/SideDrawer.component';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DataRoomShareableLink } from 'models/ShareableLink';
import { DataRoomSelectableNode } from '../components/FileCheckSelector/types';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from '../providers/DataRoom/DataRoom.context';
import { DataRoomNode } from '../models/DataRoomNode';
import { sortDataRoomNodes } from 'utils';
import { prepareNodesForShareableLinkModalFileSelector } from '../providers/EditShareableLink/utils';
import dataRoomApi from '../dataRoomApi';
import { collectCheckedNodesForShareableLink } from '../components/FileCheckSelector/utils';

export default function useShareableLink() {
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const [selectedLink, setSelectedLink] = useState<DataRoomShareableLink>();
  const [modalNodes, setModalNodes] = useState<DataRoomSelectableNode[]>([]);
  const [checkedFileIds, setCheckedFileIds] = useState<string[]>([]);
  const [checkedFolderIds, setCheckedFolderIds] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const shouldOpenDrawer = useRef(true);

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const initiallyCheckedNodes = useMemo(
    () => [...checkedFileIds, ...checkedFolderIds],
    [checkedFileIds, checkedFolderIds],
  );

  const onModalFilesSelect = useCallback(
    (nodes: DataRoomSelectableNode[]) => {
      modal?.close();

      const { fileIds, folderIds } = collectCheckedNodesForShareableLink(
        nodes,
        checkedFileIds,
      );

      setCheckedFileIds(fileIds);
      setCheckedFolderIds(folderIds);

      if (shouldOpenDrawer.current) drawer?.setOpen(true);
    },
    [checkedFileIds, drawer, modal],
  );

  const prepareInitialModalNodes = useCallback(
    (selectedFileIds: string[], nodes: DataRoomNode[]) => {
      const sortedNodes = sortDataRoomNodes(nodes);
      const pNodes = prepareNodesForShareableLinkModalFileSelector(
        sortedNodes,
        selectedFileIds,
      );

      setCheckedFileIds(selectedFileIds);
      setModalNodes(pNodes);
    },
    [],
  );

  const openModal = useCallback(
    async (selectedFileIds: string[], openDrawer = true) => {
      shouldOpenDrawer.current = openDrawer;

      modal?.open();

      try {
        setIsLoading(true);

        const { data } = await dataRoomApi.nodes.getChildren(dataRoomId);

        prepareInitialModalNodes(selectedFileIds, data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [modal, dataRoomId, prepareInitialModalNodes, popServerError],
  );

  const onCloseDrawer = useCallback(() => setModalNodes([]), [setModalNodes]);

  useEffect(() => {
    if (!selectedLink) return;

    const { fileIds, folderIds } = selectedLink;

    setCheckedFileIds(fileIds);
    setCheckedFolderIds(folderIds);
  }, [selectedLink]);

  return {
    drawer,
    selectedLink,
    modalNodes,
    isLoading,
    checkedFileIds,
    checkedFolderIds,
    initiallyCheckedNodes,
    drawerRef,
    modalRef,
    setSelectedLink,
    setModalNodes,
    setCheckedFileIds,
    setCheckedFolderIds,
    setIsLoading,
    openModal,
    onCloseDrawer,
    onModalFilesSelect,
  };
}
